import styled, { css } from "styled-components";

import { transition } from "@ui/helpers";
import { Text } from "@ui/Atoms/Text";

import { TabsWrapperProps } from "./Tabs.types";

export const Root = styled.div<{ $isDarkMode?: boolean }>`
  --tabs-button-color: ${props => props.theme.palette.grey700};
  --tabs-button-color-hover: ${props => props.theme.palette.grey800};
  --tabs-button-underscore-height: 4px;
  --tabs-transition-speed: 300ms;

  ${props =>
    props.$isDarkMode &&
    css`
      --tabs-button-color: ${props => props.theme.palette.grey100};
      --tabs-button-color-hover: ${props => props.theme.palette.white};
    `}

  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.spacing_32};

  ${props => props.theme.breakpointsV2.smallTablet} {
    gap: ${props => props.theme.spacing.spacing_24};
  }

  ${props => props.theme.breakpointsV2.smallDesktop} {
    gap: ${props => props.theme.spacing.spacing_32};
  }
`;

export const Embla = styled.div`
  position: relative;
  left: 50%;
  width: calc(var(--container-padding) * 2 + 100%);
  padding: 0 var(--container-padding);
  transform: translateX(-50%);

  ${props => props.theme.breakpointsV2.smallTablet} {
    left: 0;
    width: 100%;
    padding: 0;
    transform: none;
  }
`;

export const EmblaContent = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.spacing_24};

  ${props => props.theme.breakpointsV2.smallTablet} {
    gap: ${props => props.theme.spacing.spacing_32};
  }
`;

export const EmblaSlide = styled.div`
  flex: 0 0 auto;
`;

export const NavButton = styled(Text).attrs({
  as: "button",
  type: "button",
  $style: {
    default: "medium_16",
    smallTablet: "medium_18",
    smallDesktop: "medium_20",
  },
  $allUnset: true,
})`
  position: relative;
  padding-bottom: calc(
    var(--tabs-button-underscore-height) +
      ${props => props.theme.spacing.spacing_8}
  );
  color: var(--tabs-button-color);
  transition: ${transition("color")};
  overflow: hidden;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    ${props => props.theme.breakpointsV2.smallTablet} {
      color: var(--tabs-button-color-hover);
    }

    &::after {
      ${props => props.theme.breakpointsV2.smallTablet} {
        transform: translateY(0);
      }
    }
  }

  &[data-active="true"] {
    color: var(--tabs-button-color-hover);
    pointer-events: none;

    &::after {
      transform: translateY(0);
    }
  }

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: var(--tabs-button-underscore-height);
    background-color: var(--tabs-button-color-hover);
    transition: ${transition("transform")};
    transform: translateY(calc(100% + 2px));
  }
`;

export const TabsWrapper = styled.div<TabsWrapperProps>`
  position: relative;
  transition: height var(--tabs-transition-speed) ease-out;

  ${props =>
    !!props.$height &&
    css`
      height: ${props.$height}px;
    `}

  ${props =>
    props.$animation === "full" &&
    css`
      overflow: hidden;
      will-change: height;
    `}
`;

export const Tab = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  transition: opacity var(--tabs-transition-speed) ease-out;
  opacity: 0;
  pointer-events: none;
  z-index: 0;

  &[data-active="true"] {
    position: relative;
    transition: opacity var(--tabs-transition-speed)
      var(--tabs-transition-speed) ease-out;
    opacity: 1;
    pointer-events: auto;
    z-index: 1;
  }
`;
